import { SKY_BACKGROUND_MODES, SKY_BG_RESOLVE_SOURCES } from "../config/constants.js";

import {
  SKY_BACKGROUND_PREFS_STORAGE_KEY,
  StorageSlot,
  readStorageRecord,
  removeStorageRecord,
  writeStorageRecord,
} from "./storage.js";

export { SKY_BACKGROUND_PREFS_STORAGE_KEY, SKY_BG_RESOLVE_SOURCES };

/**
 * @param {string} [mode]
 * @returns {"auto"|"dynamic"|"static"}
 */
export function normalizeSkyBackgroundPreference(mode) {
  if (mode === SKY_BACKGROUND_MODES.STATIC) return SKY_BACKGROUND_MODES.STATIC;
  if (mode === SKY_BACKGROUND_MODES.DYNAMIC) return SKY_BACKGROUND_MODES.DYNAMIC;
  return SKY_BACKGROUND_MODES.AUTO;
}

/**
 * @param {string} [mode]
 * @returns {"dynamic"|"static"|null}
 */
export function normalizeSkyAutoResolved(mode) {
  if (mode === SKY_BACKGROUND_MODES.STATIC) return SKY_BACKGROUND_MODES.STATIC;
  if (mode === SKY_BACKGROUND_MODES.DYNAMIC) return SKY_BACKGROUND_MODES.DYNAMIC;
  return null;
}

/**
 * @param {Storage | null | undefined} [storage]
 * @returns {{ preference: "auto"|"dynamic"|"static", autoResolved: "dynamic"|"static"|null }}
 */
export function loadSkyBackgroundPrefs(storage) {
  var defaults = {
    preference: SKY_BACKGROUND_MODES.AUTO,
    autoResolved: null,
  };
  if (!storage) return defaults;
  try {
    var parsed = readStorageRecord(storage, StorageSlot.SKY_BACKGROUND);
    if (!parsed || typeof parsed !== "object") return defaults;
    return {
      preference: normalizeSkyBackgroundPreference(parsed.preference),
      autoResolved: normalizeSkyAutoResolved(parsed.autoResolved),
    };
  } catch (e) {
    return defaults;
  }
}

/**
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {Storage | null | undefined} [storage]
 */
export function saveSkyBackgroundPrefs(prefs, storage) {
  if (!storage || !prefs) return;
  try {
    writeStorageRecord(storage, StorageSlot.SKY_BACKGROUND, {
      preference: normalizeSkyBackgroundPreference(prefs.preference),
      autoResolved: normalizeSkyAutoResolved(prefs.autoResolved),
    });
  } catch (e) {
    console.warn("[sky-background-prefs] save failed:", e);
  }
}

/**
 * @param {Storage | null | undefined} [storage]
 */
export function clearSkyBackgroundPrefs(storage) {
  if (!storage) return;
  try {
    removeStorageRecord(storage, StorageSlot.SKY_BACKGROUND);
  } catch (e) { /* ignore */ }
}

/** @returns {boolean} */
export function prefersReducedMotion() {
  if (typeof window === "undefined") return false;
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch (e) {
    return false;
  }
}

/**
 * Which weak-device hint would seed static.
 * @param {{
 *   deviceMemory?: number,
 * }} [hints]
 * @returns {"low_memory"|null}
 */
export function getSkyStaticSeedReason(hints) {
  hints = hints || {};
  if (prefersReducedMotion()) return null;

  var mem = Number(hints.deviceMemory);
  if (isFinite(mem) && mem > 0 && mem < 4) return SKY_BG_RESOLVE_SOURCES.LOW_MEMORY;

  return null;
}

/**
 * Cheap bootstrap hints for weak devices — locks static in auto (skips FPS probe).
 * @param {{
 *   deviceMemory?: number,
 * }} [hints]
 */
export function shouldSeedStaticFromHints(hints) {
  return getSkyStaticSeedReason(hints) != null;
}

/**
 * @param {Window | undefined} [win]
 */
export function getSkyStaticSeedHints(win) {
  win = win || (typeof window !== "undefined" ? window : undefined);
  var nav = win && win.navigator;
  return {
    deviceMemory: nav && nav.deviceMemory != null ? Number(nav.deviceMemory) : 0,
  };
}

/**
 * Effective motion tier used by the sky clock (static vs dynamic hour crawl).
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {{
 *   forceReducedMotion?: boolean,
 *   seedStaticFromHints?: boolean,
 *   perfForcedStatic?: boolean,
 * }} [ctx]
 * @returns {"dynamic"|"static"}
 */
export function resolveSkyBackgroundEffective(prefs, ctx) {
  ctx = ctx || {};
  if (ctx.forceReducedMotion || ctx.perfForcedStatic) {
    return SKY_BACKGROUND_MODES.STATIC;
  }
  var pref = normalizeSkyBackgroundPreference(prefs && prefs.preference);
  if (pref === SKY_BACKGROUND_MODES.STATIC) return SKY_BACKGROUND_MODES.STATIC;
  if (pref === SKY_BACKGROUND_MODES.DYNAMIC) return SKY_BACKGROUND_MODES.DYNAMIC;
  if (prefs && prefs.autoResolved) return prefs.autoResolved;
  if (ctx.seedStaticFromHints) return SKY_BACKGROUND_MODES.STATIC;
  return SKY_BACKGROUND_MODES.DYNAMIC;
}

/**
 * Auto preference locked to static may recover to dynamic after a strong cycle.
 * Reduced motion and explicit static/dynamic prefs never use this path.
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {{ forceReducedMotion?: boolean }} [ctx]
 */
export function shouldAttemptSkyCycleUpgrade(prefs, ctx) {
  ctx = ctx || {};
  if (ctx.forceReducedMotion) return false;
  if (normalizeSkyBackgroundPreference(prefs && prefs.preference) !== SKY_BACKGROUND_MODES.AUTO) {
    return false;
  }
  return !!(prefs && prefs.autoResolved === SKY_BACKGROUND_MODES.STATIC);
}

/**
 * @param {{
 *   passed?: boolean,
 *   sampleCount?: number,
 * }} [result]
 * @param {{ minFrameSamples?: number }} [opts]
 */
export function shouldUpgradeSkyFromCycleResult(result, opts) {
  opts = opts || {};
  if (!result || !result.passed) return false;
  var minFrames = opts.minFrameSamples != null ? Number(opts.minFrameSamples) : 300;
  if (!isFinite(minFrames) || minFrames < 0) minFrames = 300;
  var samples = result.sampleCount != null ? Number(result.sampleCount) : 0;
  return isFinite(samples) && samples >= minFrames;
}

/**
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {{ isFirstFallOfFirstRun?: boolean }} [ctx]
 */
export function shouldRunSkyAutoProbe(prefs, ctx) {
  ctx = ctx || {};
  if (ctx.forceReducedMotion) return false;
  return normalizeSkyBackgroundPreference(prefs && prefs.preference) === SKY_BACKGROUND_MODES.AUTO
    && !(prefs && prefs.autoResolved)
    && !!ctx.isFirstFallOfFirstRun;
}

/**
 * First lifetime Fall countdown hour arc 0→STATIC_FALL_HOLD_HOUR (visual only).
 * Runs even when the FPS probe already resolved (e.g. checkpoint refresh mid-run).
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {{
 *   isFirstFallOfFirstRun?: boolean,
 *   fallState?: string,
 *   forceReducedMotion?: boolean,
 *   seedStaticFromHints?: boolean,
 *   perfForcedStatic?: boolean,
 * }} [ctx]
 */
export function shouldUseFirstFallProbeCountdownArc(prefs, ctx) {
  ctx = ctx || {};
  if (!ctx.isFirstFallOfFirstRun) return false;
  if (ctx.fallState !== "countdown") return false;
  if (normalizeSkyBackgroundPreference(prefs && prefs.preference) !== SKY_BACKGROUND_MODES.AUTO) {
    return false;
  }
  return resolveSkyBackgroundEffective(prefs, ctx) === SKY_BACKGROUND_MODES.DYNAMIC;
}

/**
 * First Fall after auto resolved to dynamic: play+settle arc continues 5→12.
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {{ isFirstFallOfFirstRun?: boolean, fallState?: string }} [ctx]
 */
export function shouldUseFirstFallSplitHourArc(prefs, ctx) {
  ctx = ctx || {};
  if (!ctx.isFirstFallOfFirstRun) return false;
  if (normalizeSkyBackgroundPreference(prefs && prefs.preference) !== SKY_BACKGROUND_MODES.AUTO) {
    return false;
  }
  if (!prefs || prefs.autoResolved !== SKY_BACKGROUND_MODES.DYNAMIC) return false;
  return ctx.fallState != null && ctx.fallState !== "countdown";
}

/**
 * @param {{ preference: string, autoResolved?: string | null }} prefs
 * @param {{
 *   forceReducedMotion?: boolean,
 *   perfForcedStatic?: boolean,
 * }} [ctx]
 * @returns {string}
 */
export function getSkyBackgroundAnalyticsMode(prefs, ctx) {
  ctx = ctx || {};
  if (ctx.forceReducedMotion || ctx.perfForcedStatic) {
    return SKY_BACKGROUND_MODES.STATIC;
  }
  var pref = normalizeSkyBackgroundPreference(prefs && prefs.preference);
  if (pref === SKY_BACKGROUND_MODES.AUTO) {
    return prefs && prefs.autoResolved ? prefs.autoResolved : "auto_pending";
  }
  return pref;
}